import React, { useEffect, useState } from 'react'
import { Container, HeaderBar, Toast as toast } from 'components'
import strings from 'strings'
import { getMyCarrierList } from 'api/carriers'
import { Alert, Box, Collapse } from '@mui/material'
import ClaimsVehiclesTable from './claimsVehiclesTable'
import ClaimsFilters from './claimsFilters'
import ClaimsMap from './claimsMap'
import moment from 'moment'
import {
  getCompanySafetyEventsTypes,
  getCompanyVehicles,
  getCompanySafetyEvents,
  getRoutesList,
} from 'api/claimsSearching'
import LoadingWrapper from 'components/loadingWrapper'
import { APIProvider } from '@vis.gl/react-google-maps'
import { VehicleEvents, VehicleRoutes } from 'interfaces/IClaimsSearching'
import {
  abbreviateThousandsNumber,
  formatEventTypeLabel,
  formatDateToUTC,
} from 'tools/helper'

interface IClaimsSearchingProps {
  carrierView?: boolean
  useKilometers?: boolean
}

const t = strings.MENU
const cs = strings.CLAIMS_SEARCHING

const getCarrierIdFromStorage = (): number | null => {
  const carrierId = localStorage.getItem('carrierId') ?? localStorage.getItem('companyId')
  if (carrierId == null || carrierId === '') return null
  const num = Number(carrierId)
  return Number.isNaN(num) ? null : num
}

const getInitialCarrierId = (): number | null => getCarrierIdFromStorage()
const getInitialCarrierName = (): string => localStorage.getItem('companyName') ?? ''

const ClaimsSearching: React.FC<IClaimsSearchingProps> = ({ carrierView = false, useKilometers = false }) => {
  const [loading, setLoading] = useState(true)
  const [carriersLoading, setCarriersLoading] = useState<boolean>(false)
  const [safetyEventsLoading, setSafetyEventsLoading] = useState<boolean>(false)
  const [vehiclesLoading, setVehiclesLoading] = useState<boolean>(false)
  const [vehicleSafetyLoading, setVehicleSafetyLoading] =
    useState<boolean>(false)
  const [routesLoading, setRoutesLoading] = useState<boolean>(false)
  const [filtersLoading, setFiltersLoading] = useState(false)

  const [carrierList, setCarrierList] = useState<any[]>([])
  const [safetyEventTypesList, setSafetyEventTypesList] = useState<any[]>([])
  const [hasSafetyEvents, setHasSafetyEvents] = useState<boolean>(false)
  const [vehicleList, setVehicleList] = useState<any[]>([])
  const initialCarrierId = carrierView ? getInitialCarrierId() : null
  const [isCarrierSelected, setIsCarrierSelected] = useState<boolean>(!!initialCarrierId)
  const [selectedCarrierId, setSelectedCarrierId] =
    useState<number | null>(() => initialCarrierId)
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
  const [selectedCarrierName, setSelectedCarrierName] = useState<string>(() =>
    carrierView ? getInitialCarrierName() : ''
  )

  const [vehiclesSafetyEventsList, setVehiclesSafetyEventsList] =
    useState<VehicleEvents | null>(null)
  const [routesList, setRoutesList] = useState<any[]>([])

  const [openAlert, setOpenAlert] = useState<boolean>(false)
  const [openDetails, setOpenDetails] = useState<boolean>(false)
  const [selectedEventData, setSelectedEventData] = useState<any>(null)

  useEffect(() => {
    if (carrierView) {
      if (selectedCarrierId != null) {
        callVehicleList(selectedCarrierId)
        const now = moment()
        const oneWeekAgo = moment().subtract(7, 'days')
        callVehiclesSafetyEventsList({
          carrierId: selectedCarrierId,
          vehicleIds: [],
          startDate: formatDateToUTC(oneWeekAgo),
          endDate: formatDateToUTC(now),
          types: [],
          limit: cs.SAFETY_EVENTS_CARRIER_LIMIT,
          includeEmptyCoordinates: false,
        })
      } else {
        callVehiclesSafetyEventsList()
      }
      callSafetyEventsTypesList()
      setLoading(false)
      return
    }
    callCarriersList()
    callSafetyEventsTypesList()
    callVehicleList()
    callVehiclesSafetyEventsList()
  }, [carrierView])

  useEffect(() => {
    if (
      vehiclesSafetyEventsList &&
      vehiclesSafetyEventsList.totalEvents >=
        (isCarrierSelected
          ? cs.SAFETY_EVENTS_CARRIER_LIMIT
          : cs.SAFETY_EVENTS_PORTFOLIO_LIMIT)
    ) {
      setOpenAlert(true)
    } else {
      setOpenAlert(false)
    }
  }, [vehiclesSafetyEventsList, isCarrierSelected])

  const callCarriersList = () => {
    setCarriersLoading(true)
    getMyCarrierList()
      .then((res: any) => {
        const carrierInfo = res?.map(mapCarrierInfo)
        setCarrierList(carrierInfo)
      })
      .finally(() => {
        setCarriersLoading(false)
        setLoading(false)
      })
  }

  const callSafetyEventsTypesList = () => {
    setSafetyEventsLoading(true)
    getCompanySafetyEventsTypes()
      .then((res: any) => {
        if (res?.listSELabel) {
          const formattedList = res.listSELabel.map(
            (item: { id: number; eventType: string }) => ({
              id: item?.id,
              eventType: formatEventTypeLabel(item?.eventType),
              type: item?.eventType,
            })
          )
          setSafetyEventTypesList(formattedList)
        }
        if (res?.hasSafetyEvents) {
          setHasSafetyEvents(res.hasSafetyEvents)
        }
      })
      .finally(() => {
        setSafetyEventsLoading(false)
        setFiltersLoading(false)
      })
  }

  const callVehicleList = (carrierId?: number) => {
    setVehiclesLoading(true)
    getCompanyVehicles(carrierId)
      .then((res: any) => {
        setVehicleList(res)
      })
      .finally(() => setVehiclesLoading(false))
  }

  const callVehiclesSafetyEventsList = (payload?: any) => {
    setVehicleSafetyLoading(true)
    const now = moment()
    const oneWeekAgo = moment().subtract(7, 'days')
    const defaultPayload: any = {
      vehicleIds: [],
      startDate: formatDateToUTC(oneWeekAgo),
      endDate: formatDateToUTC(now),
      types: [],
      limit: isCarrierSelected
        ? cs.SAFETY_EVENTS_CARRIER_LIMIT
        : cs.SAFETY_EVENTS_PORTFOLIO_LIMIT,
      includeEmptyCoordinates: false,
    }
    if (selectedCarrierId != null) {
      defaultPayload.carrierId = selectedCarrierId
    }
    const body = payload ?? defaultPayload
    if (payload && selectedCarrierId != null && payload.carrierId == null && (carrierView || isCarrierSelected)) {
      body.carrierId = selectedCarrierId
    }
    getCompanySafetyEvents(body)
      .then((res: any) => {
        setVehiclesSafetyEventsList(res)
      })
      .finally(() => setVehicleSafetyLoading(false))
  }

  const callRoutesList = (payload: any) => {
    setRoutesLoading(true)
    getRoutesList(payload)
      .then((res: any) => {
        const routesData = res as VehicleRoutes[]
        const hasRoutes =
          routesData &&
          routesData.some(
            (vehicleRoute: VehicleRoutes) => vehicleRoute.routes.length > 0
          )

        if (hasRoutes) {
          setRoutesList(routesData)
          toast({
            id: 'routesListToast',
            title: cs.ROUTES_SUCCESS_TOAST_MESSAGE,
          })
        } else {
          setRoutesList([])
          toast({
            id: 'routesListToast',
            type: 'info',
            title: cs.ROUTES_EMPTY_TOAST_MESSAGE,
          })
        }
      })
      .finally(() => setRoutesLoading(false))
  }

  const handleCarrierSelect = (carrier: any) => {
    if (carrier) {
      const { carrierId, carrierName } = carrier
      setSelectedCarrierName(carrierName)
      setSelectedCarrierId(carrierId)
      setIsCarrierSelected(true)
      callVehicleList(carrierId)
    } else {
      setSelectedCarrierName('')
      setSelectedCarrierId(null)
      setIsCarrierSelected(false)
      callVehicleList()
    }
  }

  const handleVehicleSafetyListCall = (payload: any) => {
    setVehicleSafetyLoading(true)
    callVehiclesSafetyEventsList(payload)
  }

  const handleRoutesListCall = (payload: any) => {
    setRoutesLoading(true)
    callRoutesList(payload)
  }

  const handleViewRoute = (
    vehicleId: string,
    eventTime: string,
    companyEldAuthenticationId: number
  ) => {
    const eventTimeMoment = moment.utc(eventTime)
    const timeMinus12 = eventTimeMoment.clone().subtract(12, 'hours').format('YYYY-MM-DD[T]HH:mm:ss')
    const timePlus12 = eventTimeMoment.clone().add(12, 'hours').format('YYYY-MM-DD[T]HH:mm:ss')
    const payload = {
      vehicleIds: [
        {
          assetEldId: vehicleId,
          companyEldAuthenticationId: companyEldAuthenticationId,
        },
      ],
      startDate: timeMinus12,
      endDate: timePlus12,
      distributedPoints: 30000,
    }

    setRoutesLoading(true)
    handleRoutesListCall(payload)
  }

  const handleOpenDetails = (open: boolean) => {
    setOpenDetails(open)
    if (open && selectedEventId && !selectedEventData) {
      const eventData = vehiclesSafetyEventsList?.safetyEvents?.find(
        (event) => event.id === selectedEventId
      )
      if (eventData) {
        setSelectedEventData(eventData)
      }
    }
  }

  const handleSafetyEventSelect = (
    eventId: number | null,
    openDetails?: boolean,
    showRoute?: boolean
  ) => {
    if (eventId) {
      if (selectedEventId === eventId) {
        setSelectedEventId(null)
        setSelectedEventData(null)
        handleOpenDetails(false)
        setRoutesList([])
        return
      }

      setSelectedEventId(eventId)
      const eventData = vehiclesSafetyEventsList?.safetyEvents?.find(
        (event) => event.id === eventId
      )
      if (eventData) {
        setSelectedEventData(eventData)
        if (showRoute) {
          handleViewRoute(
            eventData.vehicleId?.assetEldId,
            eventData.time,
            eventData.companyEldAuthenticationId
          )
        }
      }
      if (openDetails) {
        handleOpenDetails(true)
      }
    } else {
      setSelectedEventId(null)
      setSelectedEventData(null)
      handleOpenDetails(false)
      setRoutesList([])
    }
  }

  const resetSafetyAndRoutesList = () => {
    setVehiclesSafetyEventsList(null)
    handleSafetyEventSelect(null, false)
    setRoutesList([])
  }

  const getCarrierCode = (carrierCodes: any[]) => {
    const codePriorityOrder = ['POLICY_ID', 'DOT', 'MC', 'SCAC', 'UUID']
    const excludedCodeTypes = ['TCID']

    const carrierCode = carrierCodes.find((code: any) => {
      if (excludedCodeTypes.includes(code?.codeType)) {
        return false
      }

      return codePriorityOrder.includes(code?.codeType) || true
    })

    return carrierCode ? carrierCode?.codeValue : ''
  }

  const mapCarrierInfo = (item: any) => {
    const carrierName = item?.carrierInfo?.carrierName || 'Unknown'
    const carrierCodes = item?.carrierInfo?.carrierCodes || []
    const carrierCode = getCarrierCode(carrierCodes)
    const carrierId = item?.carrierInfo?.carrierId
    const formattedCarrierName = carrierCode
      ? `${carrierName} (${carrierCode})`
      : carrierName

    return {
      carrierId,
      carrierName: formattedCarrierName,
    }
  }

  return (
    <Container id="containterClaimsSearching" data-testid="claims-searching-page">
      <HeaderBar title={t.EVENTS_FEED} />
      <Container>
        <LoadingWrapper isLoading={filtersLoading}>
          <ClaimsFilters
            carrierView={carrierView}
            carrierSelected={isCarrierSelected}
            safetyEventsList={safetyEventTypesList}
            vehicleList={vehicleList}
            carrierList={carrierList}
            searchBtnLoading={vehicleSafetyLoading || routesLoading}
            carriersLoading={carriersLoading}
            safetyEventsLoading={safetyEventsLoading}
            vehiclesLoading={vehiclesLoading}
            onSafetySearch={handleVehicleSafetyListCall}
            onCarrierSelect={handleCarrierSelect}
            resetSafetyAndRoutesList={resetSafetyAndRoutesList}
            fixedCarrierId={carrierView ? selectedCarrierId : undefined}
            fixedCarrierName={carrierView ? selectedCarrierName : undefined}
          />
        </LoadingWrapper>
        <LoadingWrapper
          isLoading={filtersLoading || vehicleSafetyLoading || routesLoading}
        >
          <Collapse in={openAlert}>
            <Alert
              severity="warning"
              onClose={() => setOpenAlert(false)}
              sx={{ marginBottom: '1rem' }}
            >
              {cs.SAFETY_EVENTS_ALERT_CONTENT(
                abbreviateThousandsNumber(
                  isCarrierSelected
                    ? cs.SAFETY_EVENTS_CARRIER_LIMIT
                    : cs.SAFETY_EVENTS_PORTFOLIO_LIMIT
                )
              )}
            </Alert>
          </Collapse>
          <Box
            data-testid="claims-map-section"
            sx={{
              bgcolor: '#cfe8fc',
              height: '50vh',
              padding: '10px',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}
          >
            <APIProvider
              apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
            >
              <ClaimsMap
                carrierName={selectedCarrierName}
                routesList={routesList}
                safetyEventsList={vehiclesSafetyEventsList}
                selectedEventId={selectedEventId}
                selectedEventData={selectedEventData}
                onEventSelect={handleSafetyEventSelect}
                onViewRoute={handleViewRoute}
                handleOpenDetails={handleOpenDetails}
              />
            </APIProvider>
          </Box>
        </LoadingWrapper>
          <LoadingWrapper isLoading={filtersLoading || vehicleSafetyLoading}>
          <ClaimsVehiclesTable
            carrierSelected={isCarrierSelected}
            selectedCarrierId={selectedCarrierId}
            selectedCarrierName={selectedCarrierName}
            vehiclesSafetyEventsList={vehiclesSafetyEventsList}
            selectedEventId={selectedEventId}
            onEventSelect={handleSafetyEventSelect}
            onCarrierSelect={handleCarrierSelect}
            onViewRoute={handleViewRoute}
            openDetails={openDetails}
            useKilometers={useKilometers}
          />
          </LoadingWrapper>
      </Container>
    </Container>
  )
}

export default ClaimsSearching
